/**
 * Who is signed in, according to avengents-homepage's BFF.
 *
 * Sign-in is three endpoints, and only one of them is called from here — the other
 * two are navigations, because an OAuth redirect cannot be a fetch:
 *
 *   GET /auth/google/start      302 to Google        (we navigate to it)
 *   GET /auth/google/callback   sets the cookie      (Google navigates to it)
 *   GET /auth/me                200 {email} | 401    (we ask it)
 *
 * Same-origin through the dev proxy, so the session cookie is first-party and none of
 * the `SameSite` reasoning that shaped the old vault applies here.
 *
 * THREE STATES, NOT TWO, and that is the point of this module. A call to `/auth/me`
 * fails for two unrelated reasons — nobody is signed in, or the BFF is not running —
 * and they need opposite reactions: press the button, versus go and start a process.
 * Collapsing them into "signed out" produces a sign-in button that cannot work and
 * gives no hint why. That is the shape this codebase has spent days removing: an
 * answer that is confidently wrong costs more than one that says "I do not know".
 */

import { forgetPushOnThisDevice } from '../push/pushOptIn'

/**
 * Who Google says you are. `email` and `sub` always arrive; the other two do not.
 *
 * NAME AND PICTURE ARE NULLABLE, and that is a fact about Google rather than about
 * this app: an account with no profile set simply has no such claims, so the BFF
 * passes through what it was given rather than inventing a placeholder. Anything
 * rendering these has to treat absence as a case — otherwise a real account shows
 * the word "null" where its name should be.
 */
export type Identity = {
  email: string
  /** Google's stable account id. The only field safe to key on: an email can change. */
  sub: string
  name: string | null
  picture: string | null
  /**
   * The account's RevenueCat app user id — sha256 of the BFF's account key, so the
   * store never learns the Google subject (BILLING.md §2). Handed to the SDK verbatim
   * and never derived here; null on a BFF that predates billing.
   */
  billingId: string | null
  /**
   * Which door the account came in by. Two doors, two ACCOUNTS: a person who
   * signs in with Google on the web and with Apple on the phone has two of
   * everything (owner, 2026-09-10: no linking — 使用者要使用什麼帳號是他們自己的事情),
   * so the UI names the door where that would otherwise surprise.
   */
  provider: 'google' | 'apple'
}

export type Session =
  /** Signed in, with a Google-verified address. */
  | { kind: 'in'; identity: Identity }
  /** The BFF answered, and said no. Pressing sign-in is the right move. */
  | { kind: 'out' }
  /**
   * The BFF could not be reached, or answered something we cannot read. NOT the same
   * as signed out: in dev this is almost always "8092 is not running", and the fix is
   * a terminal rather than a click.
   */
  | { kind: 'unreachable'; detail: string }

/** Where the browser GOES to begin sign-in. A navigation, never a fetch — the 302 is to Google. */
export const SIGN_IN_URL = '/auth/google/start'
/** The same for Apple (guideline 4.8): a 302 to appleid.apple.com, back by a form POST. */
export const SIGN_IN_APPLE_URL = '/auth/apple/start'

/**
 * One `/auth/me` in flight at a time, shared.
 *
 * StrictMode double-invokes effects in dev, so one page load asked the same question
 * twice. Nothing was wrong with either answer — but two identical requests is two
 * console lines and one wasted round trip, and the fix is the same one used for
 * browse: share the promise, clear it on settle so a later ask is a genuine one.
 */
let inFlight: Promise<Session> | null = null

export function fetchSession(): Promise<Session> {
  if (inFlight) return inFlight
  inFlight = fetchSessionOnce().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function fetchSessionOnce(): Promise<Session> {
  let res: Response
  try {
    res = await fetch('/auth/me', {
      // The session is a cookie the BFF set; without this it is not sent and every
      // reply is 401 no matter who is signed in.
      credentials: 'include',
      // A cached 401 would outlive an actual sign-in and show a signed-out app to
      // someone who just authenticated.
      cache: 'no-store',
    })
  } catch (e) {
    return { kind: 'unreachable', detail: String(e) }
  }

  // Kept for an older BFF: a 401 still means signed out. Dropping it would make a
  // frontend newer than the service report "unreachable" for a plain signed-out
  // browser — the wrong layer, on the first screen anyone sees.
  if (res.status === 401) return { kind: 'out' }

  if (!res.ok) {
    /*
     * Any other status is the BFF being unhappy, not the user being signed out. 403
     * is the one worth naming: avengents-homepage returns it for a Google account
     * whose address is unverified, and calling that "signed out" would send someone
     * to press a button that refuses them again for a reason nobody showed them.
     */
    return { kind: 'unreachable', detail: `/auth/me answered ${res.status}` }
  }

  let body: Record<string, unknown>
  try {
    body = (await res.json()) as Record<string, unknown>
  } catch {
    return { kind: 'unreachable', detail: '/auth/me did not return JSON' }
  }

  // A 200 carrying no usable email is a broken contract, not a signed-in user.
  /*
   * Signed out is now a 200 that says so, not a 401.
   *
   * "Am I signed in?" is a question every browser may ask, and "you are nobody yet"
   * is its answer rather than a refusal. As a 401 it printed a red line in the
   * console on every first load for behaving exactly as designed — which teaches
   * people to ignore the console on the one screen where a real failure shows up.
   */
  if (body.signed_in === false) return { kind: 'out' }

  // Rendering an empty address would look like an account that has no email.
  if (typeof body.email !== 'string' || !body.email) {
    return { kind: 'unreachable', detail: '/auth/me returned 200 without an email' }
  }
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null)
  return {
    kind: 'in',
    identity: {
      email: body.email,
      // `sub` is required by the contract, but an empty string here would be worse
      // than an obvious absence: it is the field anything would key on.
      sub: str(body.sub) ?? '',
      // Normalised to null rather than left as undefined, so every consumer has one
      // absence to handle instead of two.
      name: str(body.name),
      picture: str(body.picture),
      billingId: str(body.billing_id),
      // A BFF that predates Apple sends no provider: every such session is Google's.
      provider: body.provider === 'apple' ? 'apple' : 'google',
    },
  }
}

/**
 * Sign out on the SERVER, not just in the browser.
 *
 * avengents-homepage added this rather than letting the client clear a cookie:
 * dropping the cookie locally leaves a live session id behind, and anyone who
 * captured it can still use it. The visible result is identical, which is exactly why
 * the weaker version is tempting.
 */
export async function signOut(): Promise<void> {
  /*
   * STOP THE NOTIFICATIONS FIRST, while the session that owns them still exists.
   *
   * A phone that signed out must stop receiving that account's agent
   * notifications — otherwise it keeps buzzing with someone else's work, which
   * on a shared or handed-on device is the account leaking through a channel
   * nobody is looking at. The unregister call is authorised by the very cookie
   * the next line destroys, so the order is not a preference.
   *
   * Best-effort: a sign-out must never be blocked by a notification cleanup. A
   * token left behind is dropped by Apple's own 410 Unregistered on the first
   * send after the app is removed, and by its TTL if it never is.
   */
  await forgetPushOnThisDevice()
  await fetch('/auth/signout', { method: 'POST', credentials: 'include' })
}

/**
 * Hand the BFF an Apple identity token the SHELL's native sheet produced —
 * `POST /auth/apple/native` (verified there against the bundle-id audience;
 * the authorization code is exchanged for the refresh token an account
 * deletion later revokes; the name arrives only this once). A session cookie
 * comes back like any other sign-in; the caller reloads into it.
 */
export async function signInWithAppleToken(args: {
  idToken: string
  authorizationCode?: string
  name?: string
}): Promise<boolean> {
  try {
    const res = await fetch('/auth/apple/native', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_token: args.idToken,
        ...(args.authorizationCode ? { authorization_code: args.authorizationCode } : {}),
        ...(args.name ? { name: args.name } : {}),
      }),
    })
    return res.ok
  } catch {
    return false
  }
}

/** What `POST /account/delete` answered — the three things the sheet has to say differently. */
export type DeleteOutcome =
  /** Gone: every row, every session. The caller reloads to the sign-in gate. */
  | { ok: true }
  /** A store subscription is still auto-renewing and we cannot cancel it ourselves. */
  | { ok: false; reason: 'active_subscription'; store: string }
  /** Refused or unreachable; `detail` is for the screen, in words. */
  | { ok: false; reason: 'failed'; detail: string }

/**
 * Delete the account ON THE SERVER — guideline 5.1.1(v): a person must be able to
 * start deleting their account from inside the app, and nothing weaker (an email
 * to support) counts. The BFF unpairs every machine, drops projects, messages,
 * notes, attachments and the plan row, deletes the RevenueCat customer, and ends
 * every session; only then does it answer ok. `confirm` is the word the person
 * typed, so a stray tap can never be a deletion.
 */
export async function deleteAccount(confirm: string): Promise<DeleteOutcome> {
  let res: Response
  try {
    res = await fetch('/account/delete', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm }),
    })
  } catch (e) {
    return { ok: false, reason: 'failed', detail: String(e) }
  }
  if (res.ok) return { ok: true }
  // Signed out already: there is nothing left to delete from here, and the gate is
  // where a reload lands — the caller treats this like success.
  if (res.status === 401) return { ok: true }
  let body: Record<string, unknown> = {}
  try {
    body = (await res.json()) as Record<string, unknown>
  } catch {
    /* a non-JSON refusal is still a refusal */
  }
  if (res.status === 409 && body.error === 'active_subscription') {
    return { ok: false, reason: 'active_subscription', store: typeof body.store === 'string' ? body.store : '' }
  }
  const detail = typeof body.error === 'string' ? body.error : `the server answered ${res.status}`
  return { ok: false, reason: 'failed', detail }
}
